import type {
  AddressSuggestion,
  StructuredAddress,
} from "@connect/component-library/components/address-autocomplete";
import type { InternalClient } from "@connect/data/internal/client";
import type { AddressComponent, PlaceSuggestion } from "@connect/data/internal/schema";
import { log } from "@connect/observability";
import type { CountryState } from "../countries";
import type { ResidentialAddressFields } from "./places-address.types";

const TOWN_TYPES = ["postal_town", "locality", "sublocality_level_1", "sublocality"];

export async function searchPlaces(
  client: InternalClient,
  input: string,
  sessionToken: string,
  country: string,
): Promise<AddressSuggestion[]> {
  const result = await client
    .GET("/v3/places/address-autocomplete", {
      params: { query: { input, sessionToken, country: country || undefined } },
    })
    .catch(() => null);
  if (!result?.response.ok) {
    const status = result?.response.status ?? 0;
    log.warn("connect.onboarding.address_autocomplete_failed", { status, country });
    throw new Error(`address_autocomplete_${status}`);
  }
  return (result.data?.suggestions ?? [])
    .map(toSuggestion)
    .filter((suggestion) => suggestion !== null);
}

export async function resolvePlace(
  client: InternalClient,
  place: string,
  sessionToken: string,
): Promise<StructuredAddress> {
  const result = await client
    .GET("/v3/places/address-details", { params: { query: { name: place, sessionToken } } })
    .catch(() => null);
  const components = result?.data?.addressComponents;
  if (!result?.response.ok || !components?.length) {
    const status = result?.response.status ?? 0;
    log.warn("connect.onboarding.address_details_failed", { status });
    throw new Error(`address_details_${status}`);
  }
  return parseAddressComponents(components);
}

export function toSuggestion(suggestion: PlaceSuggestion): AddressSuggestion | null {
  const prediction = suggestion.placePrediction;
  const text = prediction?.text?.text;
  if (!prediction?.place || !text) {
    return null;
  }
  const commaIndex = text.indexOf(",");
  if (commaIndex <= 0) {
    return { id: prediction.place, primary: text, secondary: "" };
  }
  return {
    id: prediction.place,
    primary: text.slice(0, commaIndex),
    secondary: text.slice(commaIndex + 1).trim(),
  };
}

export function parseAddressComponents(components: AddressComponent[]): StructuredAddress {
  const componentOf = (type: string) =>
    components.find((component) => component.types?.includes(type));
  const textOf = (type: string) => componentOf(type)?.longText ?? "";
  const state = componentOf("administrative_area_level_1");

  return {
    street: [textOf("street_number"), textOf("route")].filter(Boolean).join(" "),
    subStreet: textOf("subpremise") || undefined,
    town: TOWN_TYPES.map(textOf).find(Boolean) ?? "",
    postCode: textOf("postal_code"),
    state: state && { name: state.longText ?? "", code: state.shortText ?? "" },
  };
}

export function toResidentialAddress(
  address: StructuredAddress,
  residenceStates: CountryState[] | undefined,
): ResidentialAddressFields {
  const stateCode = address.state?.code ?? "";
  const stateBelongsToResidence =
    !residenceStates || residenceStates.some((state) => state.code === stateCode);

  return {
    street: address.street,
    subStreet: address.subStreet ?? "",
    locality: address.town,
    postalCode: address.postCode,
    administrativeArea: stateBelongsToResidence ? stateCode : "",
  };
}
